import React, { useState } from 'react';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Image from 'react-bootstrap/Image';
import { Label, Button } from 'reactstrap'; // Importing Button from Reactstrap
import { CLICK_ACTIONS, KYC_DOCUMENT_STATUS } from '../utils/Constant';
import { DocumentType } from '../utils/Types';
import {
  capitalizeFirstLetter,
  getDocumentPhotoSrc,
  isDocumentAvailable,
  isValidDocumentURL,
  toProperCase,
} from '../utils/Util';
import KYCDocDetailsModal from '../pages/modals/KYCDocDetailsModal';
import { useAppSelector } from '../store/redux-hooks';
import { candidateSelector } from '../features/assignGridSlice';

interface IKYCDocumentGalleryProps {
  onApproveRejectAction: (
    approvalStatusType: any,
    iibApprovalStatusType: number,
    documentId: number | undefined,
    comment: string
  ) => void;
  documents: DocumentType[] | undefined;
  candidateId?: number;
  roleId?: number;
}

const KYCDocumentGallery: React.FunctionComponent<IKYCDocumentGalleryProps> = ({
  onApproveRejectAction,
  documents,
  candidateId,
  roleId,
}) => {
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);
  const [selectedDocument, setSelectedDocument] = useState<DocumentType>();
  const [selectedPhotoURL, setSelectedPhotoURL] = useState<string | null>('');
  const userDetails: any = useAppSelector(candidateSelector).user;
  const togglePreviewModal = () => {
    setIsPreviewModalOpen(!isPreviewModalOpen);
  };

  const onAction = (action: string, documentId: any, comment: string) => {
    const approvalStatusType = null;
    onApproveRejectAction(
      approvalStatusType,
      action === CLICK_ACTIONS.APPROVE
        ? 2
        : action === CLICK_ACTIONS.REJECT
          ? 3
          : 4,
      documentId,
      comment
    );
  };

  const handleDownload = (url: string, filename: string) => {
    // Extract base64 string (without the prefix 'data:image/png;base64,')
    const base64Data = url;

    // Convert base64 string to a binary blob
    const byteCharacters = atob(base64Data); // Decode the base64 string into bytes
    const byteArrays = [];
    // Create a byte array for the image
    for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
      const byteArray = new Uint8Array(
        Math.min(1024, byteCharacters.length - offset)
      );
      for (let i = 0; i < byteArray.length; i++) {
        byteArray[i] = byteCharacters.charCodeAt(offset + i);
      }
      byteArrays.push(byteArray);
    }
    // Create a Blob from the byte arrays
    const blob = new Blob(byteArrays, { type: 'image/png' });

    // Create a URL for the Blob
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob); // Create an object URL for the Blob
    link.download = filename + '.png'; // Specify the download filename
    link.click(); // Trigger the download
  };

  return (
    <>
      <div className="document-parent">
        {/* IIB USER Section */}
        <Row>
          <Col xs={12}>
            {/* Only show the IIB USER label and actions if an action has been performed or roleId === 15 */}
            {(roleId === 15 || userDetails.iibapprovalstatus !== null) && (
              <>
                <div className="p-3 border rounded mt-2">
                  <div className="d-flex justify-content-center">
                    <Label className="mb-0 model-label-value mb-2">
                      IIB USER
                    </Label>
                  </div>
                  {/* Actions (Approve/Reject) are only visible for roleId === 15 */}
                  {roleId === 15 &&
                    (userDetails.iibapprovalstatus === 1 ||
                      userDetails.iibapprovalstatus == null) && (
                      <div className="d-flex justify-content-center">
                        <div
                          className="d-flex justify-content-around"
                          style={{ width: 'auto' }}
                        >
                          <Button
                            color="success"
                            className="mb-2 mx-2"
                            style={{ minWidth: '120px' }}
                            onClick={() => {
                              onAction(CLICK_ACTIONS.APPROVE, null, '');
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            color="danger"
                            className="mb-2 mx-2"
                            style={{ minWidth: '110px' }}
                            onClick={() => {
                              onAction(CLICK_ACTIONS.REJECT, null, '');
                            }}
                          >
                            Reject
                          </Button>
                          <Button
                            color="success"
                            className="mb-2 mx-2"
                            style={{ minWidth: '120px' }}
                            onClick={() => {
                              onAction(CLICK_ACTIONS.HOLD, null, '');
                            }}
                          >
                            Hold
                          </Button>
                        </div>
                      </div>
                    )}

                  {userDetails.iibapprovalstatus > 1 && (
                    <div className="d-flex justify-content-center">
                      <Label className="model-label-status mb-2">
                        {userDetails.iibapprovalstatus === 2
                          ? 'Approved'
                          : userDetails.iibapprovalstatus === 3
                            ? 'Rejected'
                            : 'On Hold'}
                      </Label>
                    </div>
                  )}
                </div>
              </>
            )}
          </Col>
        </Row>

        {/* KYC Documents Section */}
        <Label className="mb-0 kyc-documents-title mb-2">KYC Documents</Label>
        <Row>
          {isDocumentAvailable(documents) &&
            documents?.map((item: DocumentType, index: number) => {
              return (
                <Col xs={6} md={4} key={`kyc-item-${String(index)}`}>
                  <div className="p-3 border rounded mt-2">
                    <div className="d-flex justify-content-center">
                      <Label className="mb-0 model-label-value mb-2">
                        {capitalizeFirstLetter(item.documentType)}{' '}
                      </Label>
                    </div>
                    <div className="d-flex justify-content-center">
                      <div className="">
                        {item.documentStatus !==
                          String(KYC_DOCUMENT_STATUS.YET_TO_UPLOAD) &&
                        isValidDocumentURL(item.documentUrl) ? (
                          <Image
                            src={getDocumentPhotoSrc(item.documentUrl)}
                            alt={item.documentName}
                            className={
                              item.documentType.includes('Aadhaar')
                                ? 'adhar-image '
                                : 'kyc-image'
                            }
                          />
                        ) : (
                          <span className="no-document">No Document found</span>
                        )}
                      </div>
                      <div>
                        {item.documentStatus !==
                          String(KYC_DOCUMENT_STATUS.YET_TO_UPLOAD) &&
                          isValidDocumentURL(item.documentUrlBack) && (
                            <Image
                              src={getDocumentPhotoSrc(item.documentUrlBack)}
                              alt={item.documentName}
                              className="adhar-image"
                            />
                          )}
                      </div>
                    </div>
                    {(item.documentStatus ===
                      String(KYC_DOCUMENT_STATUS.APPROVED) ||
                      item.documentStatus ===
                        String(KYC_DOCUMENT_STATUS.REJECT)) && (
                      <div className="d-flex justify-content-center">
                        <Label className="model-label-status mb-2">
                          {toProperCase(item.documentStatusName)}
                        </Label>
                      </div>
                    )}

                    {item.documentStatus !==
                      String(KYC_DOCUMENT_STATUS.YET_TO_UPLOAD) &&
                      isValidDocumentURL(item.documentUrl) && (
                        <div className="d-flex justify-content-center mb-4 mt-2">
                          <Button
                            variant="primary"
                            className="cl-btn-primary"
                            onClick={() => {
                              togglePreviewModal();
                              setSelectedDocument(item);
                              console.log(item, 'whole document:');
                              setSelectedPhotoURL(item.documentUrl);
                            }}
                          >
                            View
                          </Button>
                          <Button
                            variant="primary"
                            className="cl-btn-primary"
                            onClick={() => {
                              handleDownload(
                                item.documentUrl,
                                item.documentName
                              );
                              if (item.documentType.includes('Aadhaar')) {
                                handleDownload(
                                  item.documentUrlBack,
                                  item.documentName
                                );
                              }
                            }}
                          >
                            Download
                          </Button>
                        </div>
                      )}
                  </div>
                </Col>
              );
            })}

          {!isDocumentAvailable(documents) && (
            <span className="no-document">No Documents found</span>
          )}
        </Row>
      </div>
      <KYCDocDetailsModal
        roleId={roleId}
        isOpen={isPreviewModalOpen}
        toggleApproveModal={togglePreviewModal}
        photoURL={selectedPhotoURL || ''}
        selectedDocument={selectedDocument}
        onApproveRejectAction={onApproveRejectAction}
        candidateId={candidateId}
      />
    </>
  );
};

export default KYCDocumentGallery;
